/**
 * Rena färgfunktioner för temasynkroniseringen (paletteSync.ts).
 *
 * Separat från src/components/utils/ColorUtils.tsx, som medvetet lämnas orörd
 * för sina nuvarande anropare: den ljusar ALLTID mot #FFFFFF (fel i mörkt läge),
 * returnerar tyst '#fff' för allt som inte är exakt 6-siffrig hex och tar
 * procent 0–100. Funktionerna här interpolerar mellan två godtyckliga färger i
 * båda riktningar, arbetar med t i 0–1 och kastar på ogiltig input.
 */

export type Rgb = { r: number; g: number; b: number };

/**
 * Flyttad oförändrad från appThemes.ts (samma implementation, inget nytt
 * beteende). Förutsätter 6-siffrig hex med '#'.
 */
export const hexToRgb = (hex: string): Rgb => {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
};

const clampByte = (n: number): number => Math.min(255, Math.max(0, Math.round(n)));

export const rgbToHex = ({ r, g, b }: Rgb): string =>
  `#${[clampByte(r), clampByte(g), clampByte(b)]
    .map((c) => c.toString(16).padStart(2, '0'))
    .join('')}`.toUpperCase();

const HEX6 = /^#[0-9a-f]{6}$/i;
const HEX3 = /^#[0-9a-f]{3}$/i;
const RGB_FN = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i;

/**
 * Tolererar de färgformat som faktiskt förekommer i MUI-paletter: 6-siffrig hex,
 * 3-siffrig hex samt rgb()/rgba() (MUI:s default `divider` är t.ex.
 * 'rgba(0, 0, 0, 0.12)'). Alfakanalen ignoreras — resultatet är alltid opakt.
 * Kastar på allt annat, så en felstavad token upptäcks direkt i stället för att
 * tyst bli vit.
 */
export const parseColor = (color: string): Rgb => {
  if (typeof color !== 'string') {
    // eslint-disable-next-line functional/no-throw-statement -- kontraktet är att en ogiltig färgtoken ska upptäckas direkt, inte tyst bli vit (jfr lightenColor i ColorUtils.tsx).
    throw new TypeError(`colorMath: ogiltig färg ${String(color)}`);
  }
  const value = color.trim();

  if (HEX6.test(value)) return hexToRgb(value);

  if (HEX3.test(value)) {
    const h = value.slice(1);
    return hexToRgb(`#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`);
  }

  const fn = RGB_FN.exec(value);
  if (fn) {
    return { r: Number(fn[1]), g: Number(fn[2]), b: Number(fn[3]) };
  }

  // eslint-disable-next-line functional/no-throw-statement -- se ovan.
  throw new TypeError(`colorMath: ogiltig färg "${color}"`);
};

/**
 * Linjär interpolation i sRGB mellan två färger. t = 0 ger a, t = 1 ger b.
 * Till skillnad från lightenColor() fungerar den i båda riktningar, vilket är
 * hela poängen i mörkt läge (blanda mot bakgrunden, inte mot vitt).
 */
export const mix = (a: string, b: string, t: number): string => {
  if (!Number.isFinite(t)) {
    // eslint-disable-next-line functional/no-throw-statement -- se parseColor.
    throw new TypeError(`colorMath: ogiltig blandningsfaktor ${String(t)}`);
  }
  const k = Math.min(1, Math.max(0, t));
  const ca = parseColor(a);
  const cb = parseColor(b);
  return rgbToHex({
    r: ca.r + (cb.r - ca.r) * k,
    g: ca.g + (cb.g - ca.g) * k,
    b: ca.b + (cb.b - ca.b) * k,
  });
};

/**
 * Flyttad oförändrad från appThemes.ts. Ljusar mot vitt — behålls som den var
 * eftersom deriveDark:s primary-steg är kalibrerat mot exakt detta beteende.
 */
export const lightenHex = (hex: string, amount: number): string => {
  const { r, g, b } = hexToRgb(hex);
  const lighten = (c: number) => Math.min(255, Math.round(c + (255 - c) * amount));
  return `#${[lighten(r), lighten(g), lighten(b)]
    .map((c) => c.toString(16).padStart(2, '0'))
    .join('')}`;
};

/** WCAG 2.1 relativ luminans. */
export const relLuminance = (color: string): number => {
  const { r, g, b } = parseColor(color);
  const channel = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

/** WCAG 2.1 kontrastkvot, 1–21. Ordningen på argumenten spelar ingen roll. */
export const contrastRatio = (a: string, b: string): number => {
  const la = relLuminance(a);
  const lb = relLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
};

const CONTRAST_STEP = 0.04;
// 0.96^80 = 0.038 — efter 80 steg är `toward` i praktiken nådd. Vid 25 steg låg
// 0.96^25 = 0.36 kvar, dvs. grinden kunde ge upp långt innan den nått `toward`
// och föll då tyst tillbaka på ett värde som inte klarade målet.
const MAX_CONTRAST_STEPS = 80;

/**
 * Flyttar `color` i 4%-steg mot `toward` tills kontrasten mot `bg` når `target`.
 * Grind, inte omskrivning: en färg som redan klarar målet returneras oförändrad.
 *
 * Vid MAX_CONTRAST_STEPS är `toward` i praktiken nådd. Klarar inte ens `toward`
 * målet mot `bg` är målet omöjligt för det färgparet, och `toward` returneras.
 */
export const ensureContrast = (
  color: string,
  bg: string,
  target: number,
  toward: string,
  stepsLeft: number = MAX_CONTRAST_STEPS,
): string => {
  if (stepsLeft <= 0 || contrastRatio(color, bg) >= target) return color;
  return ensureContrast(
    color === toward ? color : mix(color, toward, CONTRAST_STEP),
    bg,
    target,
    toward,
    stepsLeft - 1,
  );
};

const ON_LIGHT = '#16181A';
const ON_DARK = '#FFFFFF';

/** Väljer den av vit/nästan-svart som ger högst kontrast mot `color`. */
export const contrastOn = (color: string): string =>
  contrastRatio(color, ON_DARK) >= contrastRatio(color, ON_LIGHT) ? ON_DARK : ON_LIGHT;

export type Hsl = { h: number; s: number; l: number };

/** sRGB → HSL, h i grader (0–360), s och l i 0–1. */
export const toHsl = (color: string): Hsl => {
  const { r, g, b } = parseColor(color);
  const [rn, gn, bn] = [r / 255, g / 255, b / 255];
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h =
    max === rn
      ? ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60
      : max === gn
      ? ((bn - rn) / d + 2) * 60
      : ((rn - gn) / d + 4) * 60;
  return { h, s, l };
};

/** HSL → 6-siffrig hex. */
export const fromHsl = ({ h, s, l }: Hsl): string => {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] =
    h < 60
      ? [c, x, 0]
      : h < 120
      ? [x, c, 0]
      : h < 180
      ? [0, c, x]
      : h < 240
      ? [0, x, c]
      : h < 300
      ? [x, 0, c]
      : [c, 0, x];
  return rgbToHex({ r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 });
};

/**
 * Ljusar en färg i HSL — kulör och mättnad behålls — tills den når `target` mot
 * VARJE yta. Till skillnad från ensureContrast mot vitt, som tvättar ur färgen
 * till pastell, blir resultatet en klar och mättad nyans: det är skillnaden
 * mellan ett mörkt läge som ser skarpt ut och ett som ser grumligt ut.
 *
 * `minSaturation` lyfter färger som redan är kulörta (s > 0,15) mot den
 * mättnaden, men högst 35 % över sin egen — ett dämpat tema (slate) ska förbli
 * dämpat, bara inte grått. Gråskalor lämnas gråa.
 */
export const brightenOnDark = (
  color: string,
  surfaces: string[],
  target: number,
  minSaturation = 0.5,
): string => {
  const hsl = toHsl(color);
  const s = hsl.s > 0.15 ? Math.max(hsl.s, Math.min(minSaturation, hsl.s * 1.35)) : hsl.s;
  const meets = (c: string) => surfaces.every((bg) => contrastRatio(c, bg) >= target);
  // 1 %-steg i ljushet upp till 92 %; ovanför blir allt nästan vitt oavsett kulör.
  const steps = Array.from({ length: 93 }, (_, i) => i / 100).filter((l) => l >= hsl.l);
  const found = steps.map((l) => fromHsl({ h: hsl.h, s, l })).find(meets);
  return found ?? fromHsl({ h: hsl.h, s, l: 0.92 });
};
